import {
  deleteObject,
  getDownloadURL,
  ref,
  uploadBytes,
} from 'firebase/storage';
import { storage } from '../config/firebase';
import type {
  TenantSiteAsset,
  TenantSitePageKey,
} from '../types/site-pages';
import { prepareImageForUpload } from '../utils/storage';
import type { Client, User } from './UserService';
import { FirebaseUserContextService } from './FirebaseUserContextService';

interface UploadTenantSiteImageParams {
  assetConfig?: TenantSiteImageAssetConfig;
  clientId: string;
  pageKey: TenantSitePageKey;
  assetPath: string;
  file: File;
  user: User;
  selectedClient: Client | null;
}

export interface TenantSiteImageAssetConfig {
  label?: string;
  maxSizeBytes?: number;
  recommendedDimensions?: string;
}

export const TENANT_SITE_IMAGE_DEFAULT_MAX_SIZE_BYTES = 5 * 1024 * 1024;

export const tenantSiteImageAssetConfigs = {
  aboutTeamMember: {
    label: 'Foto da equipe',
    maxSizeBytes: 3 * 1024 * 1024,
    recommendedDimensions: '1200x1600',
  },
  homeHeroDesktop: {
    label: 'Hero desktop',
    maxSizeBytes: 5 * 1024 * 1024,
    recommendedDimensions: '1920x1080',
  },
  homeHeroMobile: {
    label: 'Hero mobile',
    maxSizeBytes: 4 * 1024 * 1024,
    recommendedDimensions: '1080x1350',
  },
  servicesHeroBackground: {
    label: 'Hero de serviços',
    maxSizeBytes: 5 * 1024 * 1024,
    recommendedDimensions: '1920x1080',
  },
} as const satisfies Record<string, TenantSiteImageAssetConfig>;

const sanitizeSegment = (value: string) =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9-_/.]+/g, '-')
    .replace(/\/+/g, '/')
    .replace(/^-+|-+$/g, '');

const getFileExtension = (file: File) => {
  const nameExtension = file.name.split('.').pop()?.trim().toLowerCase();
  if (nameExtension) {
    return nameExtension;
  }

  if (file.type === 'image/png') return 'png';
  if (file.type === 'image/webp') return 'webp';
  return 'jpg';
};

const buildTenantSiteImagePath = (
  clientId: string,
  pageKey: TenantSitePageKey,
  assetPath: string,
  file: File,
) => {
  const timestamp = Date.now();
  const randomSuffix = Math.random().toString(36).slice(2, 8);
  const extension = getFileExtension(file);
  const normalizedAssetPath = sanitizeSegment(assetPath).split('/').filter(Boolean).join('/');

  return `tenants/${clientId}/site/${pageKey}/${normalizedAssetPath}/${timestamp}-${randomSuffix}.${extension}`;
};

export class TenantSiteImageStorageService {
  static async uploadImage(
    params: UploadTenantSiteImageParams,
  ): Promise<TenantSiteAsset> {
    const {
      assetConfig,
      clientId,
      pageKey,
      assetPath,
      file,
      user,
      selectedClient,
    } = params;

    if (!clientId) {
      throw new Error('Tenant não informado para o upload da imagem.');
    }

    await FirebaseUserContextService.syncAuthContext(user, selectedClient);

    const preparedFile = await prepareImageForUpload(
      file,
      assetConfig?.maxSizeBytes ?? TENANT_SITE_IMAGE_DEFAULT_MAX_SIZE_BYTES,
    );
    const storagePath = buildTenantSiteImagePath(clientId, pageKey, assetPath, preparedFile);
    const storageRef = ref(storage, storagePath);

    await uploadBytes(storageRef, preparedFile, {
      contentType: preparedFile.type,
      customMetadata: {
        clientId,
        tenantId: clientId,
        pageKey,
        assetPath: sanitizeSegment(assetPath),
        uploadedByUserId: user.userId,
        uploadedByFirebaseUid: user.firebase_uid || '',
        uploadedByName: user.userDisplayName || '',
        originalName: file.name,
      },
    });

    const downloadUrl = await getDownloadURL(storageRef);

    return {
      provider: 'firebase-storage',
      storagePath,
      downloadUrl,
      alt: '',
      metadata: {
        pageKey,
        assetPath: sanitizeSegment(assetPath),
        uploadedByUserId: user.userId,
      },
    };
  }

  static async deleteImage(
    asset: TenantSiteAsset | null | undefined,
    user: User | null,
    selectedClient: Client | null,
  ): Promise<void> {
    if (!asset?.storagePath || asset.provider !== 'firebase-storage' || !user) {
      return;
    }

    await FirebaseUserContextService.syncAuthContext(user, selectedClient);
    await deleteObject(ref(storage, asset.storagePath));
  }
}
